/**
 * Process canvas JSON data by merging part_details from current/past canvas data.
 * Used by CanvasNode (Documents) and CanvasForm (Canvas edit mode) to render
 * canvas pages with latest part details instead of snapshot URLs.
 *
 * @param {Object} data - Raw canvas JSON (from canvas_json_data_file)
 * @param {Object} sourceData - Source with part_details (e.g. current_canvas_data_json or past_canvas_data_json from procedure-nodes API)
 * @returns {Array} Processed pages array with partsByType updated from part_details
 */
export function processCanvasJsonWithPartDetails(data, sourceData) {
  if (!data) return [];

  const partDetailsMap = new Map();
  if (sourceData?.part_details && Array.isArray(sourceData.part_details)) {
    sourceData.part_details.forEach((part) => {
      partDetailsMap.set(part.part_ref, part);
    });
  }

  const pages = Array.isArray(data)
    ? data
    : data?.results?.pages || data?.pages || data?.data?.pages || [];

  return pages.map((page) => {
    const shapeData = page.shape_data || page.shapes || [];
    const processedShapeData = shapeData.map((shape) => {
      if (
        (shape.type === "Text" || shape.type === "text_box") &&
        (shape.shape_properties?.partsByType ||
          shape.shape_properties?.shape_info?.partsByType ||
          shape.shape_info?.partsByType)
      ) {
        const props = shape.shape_properties || shape.shape_info;
        const partsByType = props.partsByType || props.shape_info?.partsByType;
        const newPartsByType = {};

        Object.keys(partsByType).forEach((oldType) => {
          const group = partsByType[oldType];
          if (group && group.parts && Array.isArray(group.parts)) {
            group.parts.forEach((partItem) => {
              const matchingPart = partDetailsMap.get(partItem.part_ref);

              let targetType = oldType;
              let targetOrder = group.order;
              let updatedPartItem = { ...partItem };
              let hasExplicitOrder = false;

              if (matchingPart) {
                let newPartStr = "";
                if (matchingPart.part_number) {
                  newPartStr += `(${matchingPart.part_number}) `;
                }
                if (matchingPart.description) {
                  newPartStr += matchingPart.description;
                }

                updatedPartItem = {
                  ...partItem,
                  part: newPartStr || partItem.part,
                  note: partItem.note,
                  itemCodeNote:
                    matchingPart.textbox_note || partItem.itemCodeNote,
                  textbox_note:
                    matchingPart.textbox_note || partItem.textbox_note,
                  type_name:
                    matchingPart.type_name || partItem.type_name,
                };

                if (matchingPart.type_name) {
                  targetType = matchingPart.type_name;
                }

                if (matchingPart.type_info?.order !== undefined) {
                  targetOrder = matchingPart.type_info.order;
                  hasExplicitOrder = true;
                }
              } else {
                if (oldType === targetType && group.order !== undefined) {
                  hasExplicitOrder = true;
                }
              }

              if (!newPartsByType[targetType]) {
                newPartsByType[targetType] = {
                  order: targetOrder,
                  parts: [],
                };
              } else if (hasExplicitOrder) {
                newPartsByType[targetType].order = targetOrder;
              }

              newPartsByType[targetType].parts.push(updatedPartItem);
            });
          }
        });

        if (shape.shape_properties) {
          const updatedShapeProperties = {
            ...shape.shape_properties,
            partsByType: newPartsByType,
          };
          if (shape.shape_properties.shape_info) {
            updatedShapeProperties.shape_info = {
              ...shape.shape_properties.shape_info,
            };
          }
          return {
            ...shape,
            shape_properties: updatedShapeProperties,
          };
        }
        return {
          ...shape,
          shape_info: {
            ...shape.shape_info,
            partsByType: newPartsByType,
          },
        };
      }
      return shape;
    });

    const processedTextBoxData = (page.text_box_data || []).map((textBox) => {
      const contentData = textBox.text_box_content_data || [];
      if (contentData.length === 0) return textBox;

      const processedContent = contentData.map((entry) => {
        const hasInlinePartInfo = !!entry.part_info;
        const details = hasInlinePartInfo
          ? entry.part_info
          : partDetailsMap.get(entry.part_ref);
        if (!details) return entry;

        let newPartStr = "";
        if (details.part_number) {
          newPartStr += `(${details.part_number}) `;
        }
        if (details.description) {
          newPartStr += details.description;
        }

        return {
          ...entry,
          part: newPartStr || entry.part,
          itemCodeNote: details.textbox_note || entry.itemCodeNote,
          textbox_note: details.textbox_note || entry.textbox_note,
          type_name: details.type_name || entry.type_name,
        };
      });

      return {
        ...textBox,
        text_box_content_data: processedContent,
      };
    });

    return {
      ...page,
      [page.shape_data ? "shape_data" : "shapes"]: processedShapeData,
      text_box_data: processedTextBoxData.length > 0 ? processedTextBoxData : page.text_box_data,
    };
  });
}
